// doctor-frontend/src/pages/intern/perf/PerformanceReviewDetail.jsx
// SR → Faculty performance review detail.
// No-regression patch:
// - Keeps the existing load/return/finalize workflow and internApi method names.
// - Adds tenant slug fallback from storage so navigation keeps /t/:slug after redirects.
// - Adds safer role/status handling for Faculty/Admin/HOD/Owner review actions.
// - Adds Core Practice Area-safe display sanitization.
// - Handles multiple backend response shapes without changing backend contracts.

import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import UndoIcon from '@mui/icons-material/Undo';
import { internApi } from '../../../services/internApi';

const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = ['Entrustable', 'Professional', 'Activities'].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value || 'null');
  } catch {
    return null;
  }
}

function sanitizeStringArray(values) {
  if (!Array.isArray(values)) return [];
  return values.map((value) => sanitizeDisplayText(value)).filter(Boolean);
}

function normalizeRoleToken(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function readStoredUser() {
  const keys = [
    'loggedInUser',
    'user',
    'currentUser',
    'authUser',
    'cmfmUser',
    'portalUser',
  ];

  try {
    for (const key of keys) {
      const local = safeJsonParse(localStorage.getItem(key));
      const session = safeJsonParse(sessionStorage.getItem(key));
      const hit = local || session;
      if (hit) return hit?.user ? { ...hit.user, ...hit } : hit;
    }
  } catch {
    // ignore storage failures
  }

  return {};
}

function getRole() {
  const user = readStoredUser();
  const direct =
    user?.role ||
    user?.userType ||
    user?.profile?.role ||
    user?.user?.role ||
    '';

  if (direct) return sanitizeDisplayText(direct);

  try {
    return sanitizeDisplayText(
      localStorage.getItem('role') ||
        localStorage.getItem('userRole') ||
        sessionStorage.getItem('role') ||
        sessionStorage.getItem('userRole') ||
        ''
    );
  } catch {
    return '';
  }
}

function isFacultyReviewerRole(role) {
  const r = normalizeRoleToken(role);
  return (
    r === 'faculty' ||
    r === 'admin' ||
    r === 'administrator' ||
    r === 'hod' ||
    r === 'head of department' ||
    r === 'owner' ||
    r === 'super admin' ||
    r.includes('faculty') ||
    r.includes('admin') ||
    r.includes('hod') ||
    r.includes('owner')
  );
}

function persistTenantSlug(slug) {
  try {
    const s = String(slug || '').trim();
    if (!s) return;
    localStorage.setItem('tenantSlug', s);
    localStorage.setItem('activeTenantSlug', s);
    sessionStorage.setItem('tenantSlug', s);
    sessionStorage.setItem('activeTenantSlug', s);
  } catch {
    // ignore storage failures
  }
}

function readTenantSlugFromStorage() {
  const candidates = [];

  try {
    [
      localStorage.getItem('tenantSlug'),
      localStorage.getItem('activeTenantSlug'),
      sessionStorage.getItem('tenantSlug'),
      sessionStorage.getItem('activeTenantSlug'),
    ]
      .filter(Boolean)
      .forEach((value) => candidates.push(value));

    [
      safeJsonParse(localStorage.getItem('loggedInUser')),
      safeJsonParse(sessionStorage.getItem('loggedInUser')),
      safeJsonParse(localStorage.getItem('user')),
      safeJsonParse(sessionStorage.getItem('user')),
      safeJsonParse(localStorage.getItem('currentUser')),
      safeJsonParse(sessionStorage.getItem('currentUser')),
    ]
      .filter(Boolean)
      .forEach((obj) => {
        const core = obj?.user || obj;
        candidates.push(
          core?.tenantSlug,
          obj?.tenantSlug,
          core?.tenant?.slug,
          obj?.tenant?.slug
        );
      });
  } catch {
    // ignore
  }

  return candidates
    .map((value) => String(value || '').trim())
    .find((slug) => /^[a-z0-9-]{2,80}$/i.test(slug)) || '';
}

/** Same tenantUrl style used across intern pages; keeps /t/:slug and prevents double prefixing. */
function tenantUrl(path) {
  if (!path) return path;
  const s = String(path);

  if (/^https?:\/\//i.test(s)) return s;

  const p = s.startsWith('/') ? s : `/${s}`;
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    if (m) {
      const prefix = m[0];
      const slug = prefix.replace(/^\/t\//, '');
      persistTenantSlug(slug);
      if (!p.startsWith(`${prefix}/`)) return `${prefix}${p}`.replace(/\/{2,}/g, '/');
      return p;
    }
  } catch {
    // fall through to storage fallback
  }

  const slug = readTenantSlugFromStorage();
  if (!slug) return p;
  const prefix = `/t/${slug}`;
  if (p.startsWith(`${prefix}/`)) return p;
  return `${prefix}${p}`.replace(/\/{2,}/g, '/');
}

function normalizeStatusToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
    .replace(/-+/g, '-');
}

function statusLabel(value) {
  const s = normalizeStatusToken(value);
  if (s === 'sr-submitted') return 'Submitted by SR';
  if (s === 'submitted-to-faculty' || s === 'faculty-review' || s === 'faculty-pending') return 'Awaiting faculty review';
  if (s === 'returned' || s === 'returned-to-sr') return 'Returned to SR';
  if (s === 'finalized' || s === 'approved' || s === 'faculty-finalized') return 'Finalized';
  if (s === 'draft') return 'Draft';
  return sanitizeDisplayText(value) || '-';
}

function statusChipColor(value) {
  const s = normalizeStatusToken(value);
  if (s.includes('final') || s === 'approved') return 'success';
  if (s.includes('submit') || s.includes('pending') || s.includes('review')) return 'warning';
  if (s.includes('return')) return 'default';
  return 'default';
}

function isReturnableStatus(value) {
  const s = normalizeStatusToken(value);
  return ['sr-submitted', 'submitted-to-faculty', 'faculty-review', 'faculty-pending'].includes(s);
}

function isFinalizableStatus(value) {
  const s = normalizeStatusToken(value);
  return ['sr-submitted', 'submitted-to-faculty', 'faculty-review', 'faculty-pending', 'returned', 'returned-to-sr'].includes(s);
}
function pickReviewDoc(res) {
  const item = res?.item || res?.data || res?.review || res?.doc || res || null;
  if (!item || typeof item !== 'object') return null;
  return {
    ...item,
    status: sanitizeDisplayText(item.status || ''),
    summaryNotes: sanitizeDisplayText(item.summaryNotes || item.notes || ''),
    competencies: Array.isArray(item.competencies)
      ? item.competencies.map((c, index) => ({
          ...c,
          key: c?.key || c?._id || `competency-${index + 1}`,
          title: sanitizeDisplayText(c?.title || c?.name || c?.label || `Competency ${index + 1}`),
          notes: sanitizeDisplayText(c?.notes || c?.remark || c?.comments || ''),
          score: Number.isFinite(Number(c?.score)) ? Number(c.score) : 0,
        }))
      : [],
  };
}

function personLabel(value) {
  if (!value) return '';
  if (typeof value === 'string') return sanitizeDisplayText(value);
  if (typeof value === 'object') {
    return sanitizeDisplayText(
      value.name ||
        value.fullName ||
        value.displayName ||
        value.email ||
        value.username ||
        value.userId ||
        value._id ||
        value.id ||
        ''
    );
  }
  return sanitizeDisplayText(value);
}

function dateLabel(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return sanitizeDisplayText(value);
    return d.toDateString();
  } catch {
    return sanitizeDisplayText(value);
  }
}

function clampScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(5, n));
}

export default function PerformanceReviewDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [facultyNotes, setFacultyNotes] = useState('');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const actedOnce = useRef(false);

  const role = normalizeRoleToken(getRole());
  const isFaculty = isFacultyReviewerRole(role);

  const returnTo = useMemo(() => {
    const fromState = location?.state?.from;
    if (fromState && typeof fromState === 'string') return fromState;
    try {
      return `${location.pathname || ''}${location.search || ''}${location.hash || ''}`;
    } catch {
      return '';
    }
  }, [location?.state, location.pathname, location.search, location.hash]);

  const queuePath = tenantUrl('/intern/perf/review');
  const reviewerWorkbenchPath = tenantUrl('/dashboard/approval');
  const activityLogPath = tenantUrl('/intern/activity/log');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    setOk('');
    try {
      const res = await internApi.perfGetReview(id);
      const item = pickReviewDoc(res);
      setDoc(item);
      setFacultyNotes(
        sanitizeDisplayText(
          item?.facultyReviewer?.notes ||
            item?.facultyNotes ||
            item?.decisionNotes ||
            ''
        )
      );
    } catch (e) {
      setDoc(null);
      setErr(e?.message || 'Failed to load review');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const avg = useMemo(() => {
    const values = (doc?.competencies || [])
      .map((c) => Number(c.score))
      .filter((score) => Number.isFinite(score));

    if (!values.length) return '—';

    const total = values.reduce((sum, score) => sum + score, 0);
    return (total / values.length).toFixed(1);
  }, [doc]);

  const status = normalizeStatusToken(doc?.status);
  const canReturn = isFaculty && isReturnableStatus(status);
  const canFinalize = isFaculty && isFinalizableStatus(status);

  async function doReturn() {
    if (!canReturn || actedOnce.current) return;
    setActing(true);
    setErr('');
    setOk('');
    try {
      actedOnce.current = true;
      await internApi.perfReturnReview(id, { notes: facultyNotes });
      setOk('Returned to SR.');
      await load();
    } catch (e) {
      setErr(e?.message || 'Failed to return');
    } finally {
      setActing(false);
      setTimeout(() => {
        actedOnce.current = false;
      }, 500);
    }
  }

  async function finalize() {
    if (!canFinalize || actedOnce.current) return;
    setActing(true);
    setErr('');
    setOk('');
    try {
      actedOnce.current = true;
      await internApi.perfFinalizeReview(id, { notes: facultyNotes });
      setOk('Finalized successfully.');
      await load();
    } catch (e) {
      setErr(e?.message || 'Failed to finalize');
    } finally {
      setActing(false);
      setTimeout(() => {
        actedOnce.current = false;
      }, 500);
    }
  }

  function goBack() {
    if (returnTo && returnTo !== location.pathname) {
      navigate(returnTo);
      return;
    }
    navigate(queuePath);
  }

  if (loading) {
    return (
      <Box className="flex items-center gap-2 opacity-80 text-sm p-4">
        <CircularProgress size={16} /> Loading review…
      </Box>
    );
  }

  if (!doc) {
    return (
      <Box className="space-y-3 p-4">
        <Alert severity="error">{err || 'Review not found'}</Alert>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={goBack}>
          Back to queue
        </Button>
      </Box>
    );
  }

  const internName = personLabel(doc.internName || doc.internUserId || doc.internUser);
  const srReviewer = personLabel(doc.srReviewer || doc.srReviewer?.name || doc.srReviewer?.userId);
  const facultyReviewer = personLabel(doc.facultyReviewer || doc.facultyReviewer?.name || doc.facultyReviewer?.userId);
  const competencyTitles = sanitizeStringArray((doc.competencies || []).map((c) => c.title));

  return (
    <Box className="space-y-4 p-4 md:p-6">
      <Box className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <Box>
          <Typography variant="h5" className="font-semibold">
            Performance Review
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.5 }}>
            SR to Faculty performance review record. This review workflow is kept separate from guided activity evidence and citizen OPD visit workflow.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" startIcon={<ArrowBackIcon />} onClick={goBack}>
            Back
          </Button>
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading || acting}>
            Refresh
          </Button>
          <Button size="small" variant="outlined" component={Link} to={reviewerWorkbenchPath}>
            Reviewer Workbench
          </Button>
          <Button size="small" variant="outlined" component={Link} to={activityLogPath}>
            Activity Log
          </Button>
        </Stack>
      </Box>

      {err && <Alert severity="error" onClose={() => setErr('')}>{err}</Alert>}
      {ok && <Alert severity="success" onClose={() => setOk('')}>{ok}</Alert>}

      {!isFaculty && (
        <Alert severity="info">
          You can view this performance review, but only Faculty/Admin/HOD-level reviewer roles can return or finalize it.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }}>
          <Chip label={statusLabel(doc.status)} color={statusChipColor(doc.status)} />
          <Chip label={`Average: ${avg}`} variant="outlined" />
          {competencyTitles.length > 0 && (
            <Chip label={`${competencyTitles.length} competencies`} variant="outlined" />
          )}
        </Stack>

        <div className="text-sm opacity-80">
          Intern: <b>{internName || '—'}</b> • Period:{' '}
          {dateLabel(doc.periodStart)} → {dateLabel(doc.periodEnd)}
        </div>
      </Paper>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <TextField
          label="Status"
          value={statusLabel(doc.status)}
          InputProps={{ readOnly: true }}
        />
        <TextField
          label="Average Score"
          value={avg}
          InputProps={{ readOnly: true }}
        />
        <TextField
          label="SR Reviewer"
          value={srReviewer || '—'}
          InputProps={{ readOnly: true }}
        />
      </div>

      {facultyReviewer && (
        <TextField
          label="Faculty Reviewer"
          value={facultyReviewer}
          InputProps={{ readOnly: true }}
          fullWidth
        />
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Competency Scores
        </Typography>

        {(doc.competencies || []).length === 0 ? (
          <Alert severity="info">No competency scores are recorded in this review.</Alert>
        ) : (
          <Stack spacing={2}>
            {(doc.competencies || []).map((c, index) => {
              const score = clampScore(c.score);
              return (
                <Box key={c.key || index} className="p-3 border rounded-md dark:border-neutral-700">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {c.title || `Competency ${index + 1}`}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 0.5 }}>
                    <Box sx={{ width: { xs: 180, md: 280 } }}>
                      <Slider value={score} min={0} max={5} step={1} marks disabled />
                    </Box>
                    <Chip size="small" label={`${score}/5`} />
                  </Stack>
                  {c.notes && (
                    <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.85 }}>
                      {c.notes}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>

      <TextField
        label="Summary Notes"
        value={doc.summaryNotes || ''}
        InputProps={{ readOnly: true }}
        fullWidth
        multiline
        minRows={3}
      />

      {isFaculty && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Faculty Decision
          </Typography>
          <TextField
            label="Faculty Notes / Decision"
            value={facultyNotes}
            onChange={(e) => setFacultyNotes(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            disabled={acting}
          />

          <Divider sx={{ my: 2 }} />

          {!canReturn && !canFinalize && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No faculty action is currently available for status: {statusLabel(doc.status)}.
            </Alert>
          )}

          <div className="flex flex-wrap gap-2">
            {canReturn && (
              <Tooltip title="Return this review to SR for correction">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<UndoIcon />}
                    onClick={doReturn}
                    disabled={acting}
                  >
                    {acting ? 'Working…' : 'Return to SR'}
                  </Button>
                </span>
              </Tooltip>
            )}

            {canFinalize && (
              <Tooltip title="Finalize this performance review">
                <span>
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    onClick={finalize}
                    disabled={acting}
                  >
                    {acting ? 'Working…' : 'Finalize (Faculty)'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
        </Paper>
      )}
    </Box>
  );
}
